/**
 * 請求書の印刷ページ(2026-10-23 段階3・/invoice/:id)。サイドバーなしの単独レイアウト(受領書の印刷ページと同じ作り)。
 * 「印刷する」からブラウザの印刷で PDF にもできる。正式な見本(発注者が用意)が届いたら書式を合わせる(それまでは仮の書式)。
 * 発行者の情報(正式名称・住所・登録番号・振込先)は、発行した時点の写し(invoice.issuer)を出す。
 * 写しの無い古い請求書だけマスタ管理の「会社」の今の情報を出す。未入力の欄は出さない
 */
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { getDb } from '@core/lib/firebase';
import { useMasters } from '@core/masters/MastersProvider';
import { formatYen } from '@core/lib/money';
import type { Invoice } from '@core/lib/types';
import { toMonthLabel } from '@core/lib/date';
import { INVOICE_STATUS_LABELS, issuerOf } from './invoice';

function jaDate(d: string): string {
  const [y, m, day] = d.split('-').map(Number);
  return `${y}年${m}月${day}日`;
}

export default function InvoicePrintPage() {
  const { id } = useParams();
  const { companies } = useMasters();
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    getDoc(doc(getDb(), 'invoices', id))
      .then((snap) => {
        if (!snap.exists()) setError('請求書が見つかりません');
        else setInvoice({ id: snap.id, ...(snap.data() as object) } as Invoice);
      })
      .catch((e) => setError(e instanceof Error ? e.message : '読み込めませんでした'));
  }, [id]);

  if (error) return <p style={{ padding: 24 }}>{error}</p>;
  if (!invoice) return <p style={{ padding: 24 }}>読み込み中…</p>;

  const company = companies.find((c) => c.id === invoice.company_id);
  const issuer = invoice.issuer ?? (company ? issuerOf(company) : null);

  return (
    <div style={{ background: '#fff', minHeight: '100vh', color: '#1a1a1a', fontFamily: '"Hiragino Sans", "Yu Gothic", sans-serif' }}>
      <style>{`
        @media print { .no-print { display: none !important; } body { background: #fff; } }
        @page { size: A4 portrait; margin: 16mm; }
        .inv-sheet { max-width: 720px; margin: 16px auto; padding: 0 20px; font-size: 13px; }
        .inv-sheet table { width: 100%; border-collapse: collapse; }
        .inv-sheet table tr > th, .inv-sheet table tr > td { border: 1px solid #999; padding: 6px 8px; }
        .inv-sheet table tr > th { background: #f2f2f2; font-weight: 600; color: #1a1a1a; font-size: 13px; text-align: left; }
        .inv-sheet .r { text-align: right; font-variant-numeric: tabular-nums; white-space: nowrap; }
        @media (max-width: 600px) { .inv-sheet { font-size: 12px; padding: 0 10px; } .inv-head { flex-direction: column; } }
      `}</style>

      <div className="no-print" style={{ padding: '12px 16px', background: '#F4F7FB', borderBottom: '1px solid #ddd', display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <b>請求書 {invoice.number}</b>
        {invoice.status === 'void' && <span style={{ color: '#c00' }}>(取消済み)</span>}
        <span style={{ fontSize: 12.5, color: '#666' }}>「印刷する」で PDF にも保存できます(仮の書式)</span>
        <button
          onClick={() => window.close()}
          style={{ marginLeft: 'auto', padding: '8px 18px', background: '#fff', color: '#024BD5', border: '1px solid #024BD5', borderRadius: 6, fontSize: 14, cursor: 'pointer' }}
        >
          閉じる
        </button>
        <button
          onClick={() => window.print()}
          style={{ padding: '8px 22px', background: '#024BD5', color: '#fff', border: 'none', borderRadius: 6, fontSize: 14, cursor: 'pointer' }}
        >
          印刷する
        </button>
      </div>

      <div className="inv-sheet">
        <h1 style={{ textAlign: 'center', fontSize: 26, letterSpacing: 8, margin: '20px 0 24px' }}>
          請求書{invoice.status === 'void' ? '(取消)' : ''}
        </h1>

        <div className="inv-head" style={{ display: 'flex', justifyContent: 'space-between', gap: 24 }}>
          <div style={{ flex: 1 }}>
            {invoice.customer_postal_code && <div>〒{invoice.customer_postal_code}</div>}
            {invoice.customer_address && <div>{invoice.customer_address}</div>}
            <div style={{ fontSize: 18, fontWeight: 700, borderBottom: '1px solid #333', padding: '6px 0', marginTop: 6 }}>
              {invoice.customer_addressee || `${invoice.customer_name} 御中`}
            </div>
            <p style={{ marginTop: 12 }}>下記のとおりご請求申し上げます。</p>
          </div>
          <div style={{ minWidth: 240 }}>
            <div>請求書番号: {invoice.number}</div>
            <div>発行日: {jaDate(invoice.issue_date)}</div>
            <div>対象: {toMonthLabel(invoice.period_month)}分</div>
            <div style={{ marginTop: 10, fontWeight: 700 }}>{issuer?.name ?? ''}</div>
            {issuer?.postal_code && <div>〒{issuer.postal_code}</div>}
            {issuer?.address && <div>{issuer.address}</div>}
            {issuer?.tel && <div>TEL {issuer.tel}</div>}
            {issuer?.invoice_reg_no && <div>登録番号: {issuer.invoice_reg_no}</div>}
          </div>
        </div>

        <table className="no-stack" style={{ margin: '18px 0 6px', width: 'auto', minWidth: 280 }}>
          <tbody>
            <tr>
              <th>ご請求金額(税込)</th>
              <td className="r" style={{ fontSize: 18, fontWeight: 700 }}>
                ¥{formatYen(invoice.total)}
              </td>
            </tr>
            <tr>
              <th>お支払期限</th>
              <td>{jaDate(invoice.due_date)}</td>
            </tr>
          </tbody>
        </table>

        <table className="no-stack" style={{ marginTop: 14 }}>
          <thead>
            <tr>
              <th style={{ width: 64 }}>日付</th>
              <th>内容</th>
              <th style={{ width: 110 }}>金額(税込)</th>
            </tr>
          </thead>
          <tbody>
            {invoice.lines.map((l, i) => (
              <tr key={`${l.transaction_id ?? i}`}>
                <td>{l.date.slice(5).replace('-', '/')}</td>
                <td>{l.description}</td>
                <td className="r">{formatYen(l.amount)}</td>
              </tr>
            ))}
            <tr>
              <td colSpan={2} className="r">
                合計(税込)
              </td>
              <td className="r" style={{ fontWeight: 700 }}>
                {formatYen(invoice.total)}
              </td>
            </tr>
            <tr>
              <td colSpan={2} className="r">
                うち消費税({invoice.tax_rate}%対象 {formatYen(invoice.total)})
              </td>
              <td className="r">{formatYen(invoice.tax_amount)}</td>
            </tr>
          </tbody>
        </table>

        {issuer?.bank_info && (
          <div style={{ marginTop: 16 }}>
            <b>お振込先</b>
            <div style={{ whiteSpace: 'pre-wrap' }}>{issuer.bank_info}</div>
          </div>
        )}
        {invoice.note && (
          <div style={{ marginTop: 12 }}>
            <b>備考</b>
            <div style={{ whiteSpace: 'pre-wrap' }}>{invoice.note}</div>
          </div>
        )}
        <p className="no-print" style={{ marginTop: 24, color: '#888', fontSize: 12 }}>
          状態: {INVOICE_STATUS_LABELS[invoice.status]}(この行は印刷されません)
        </p>
      </div>
    </div>
  );
}
